import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  User, 
  Globe, 
  Check, 
  RefreshCw, 
  Save,
  AlertCircle
} from 'lucide-react';

const AuthorSettings = ({ userId = null, isAdmin = false, onUpdate = null }) => {
  const [sites, setSites] = useState([]);
  const [mappings, setMappings] = useState({});  // {site_id: {wp_author_id, wp_author_name}}
  const [editValues, setEditValues] = useState({});  // {site_id: {id: '', name: ''}}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  
  // Charger les sites WordPress
  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wordpress-sites`);
      setSites(response.data || []);
    } catch (e) {
      console.error('Erreur chargement sites:', e);
    }
  }, []);
  
  // Charger les mappings de l'utilisateur
  const fetchMappings = useCallback(async () => {
    try {
      const endpoint = userId 
        ? `${API}/user-author-mappings/by-user/${userId}`
        : `${API}/user-author-mappings`;
      const response = await axios.get(endpoint);
      
      // Convertir en objet indexé par site_id
      const mappingsObj = {};
      const editObj = {};
      (response.data || []).forEach(m => {
        mappingsObj[m.site_id] = {
          wp_author_id: m.wp_author_id,
          wp_author_name: m.wp_author_name,
          wp_author_slug: m.wp_author_slug
        };
        editObj[m.site_id] = {
          id: String(m.wp_author_id),
          name: m.wp_author_name || ''
        };
      });
      setMappings(mappingsObj);
      setEditValues(editObj);
    } catch (e) {
      console.error('Erreur chargement mappings:', e);
    }
    setLoading(false);
  }, [userId]);
  
  useEffect(() => {
    fetchSites();
    fetchMappings();
  }, [fetchSites, fetchMappings]);
  
  // Mettre à jour les valeurs d'édition
  const handleInputChange = (siteId, field, value) => {
    setEditValues(prev => ({
      ...prev,
      [siteId]: {
        ...(prev[siteId] || { id: '', name: '' }),
        [field]: value
      }
    }));
  };
  
  // Sauvegarder une association
  const handleSaveMapping = async (siteId) => {
    const values = editValues[siteId];
    if (!values?.id || !values?.name) {
      toast.error('Veuillez renseigner l\'ID et le nom de l\'auteur');
      return;
    }
    
    const authorId = parseInt(values.id, 10);
    if (isNaN(authorId) || authorId <= 0) {
      toast.error('L\'ID WordPress doit être un nombre positif');
      return;
    }
    
    setSaving(prev => ({ ...prev, [siteId]: true }));
    try {
      const endpoint = isAdmin && userId
        ? `${API}/user-author-mappings/admin`
        : `${API}/user-author-mappings`;
      
      const payload = isAdmin && userId
        ? { user_id: userId, site_id: siteId, wp_author_id: authorId, wp_author_name: values.name, wp_author_slug: '' }
        : { site_id: siteId, wp_author_id: authorId, wp_author_name: values.name, wp_author_slug: '' };
      
      const method = isAdmin && userId ? 'put' : 'post';
      await axios[method](endpoint, payload);
      
      setMappings(prev => ({
        ...prev,
        [siteId]: { wp_author_id: authorId, wp_author_name: values.name, wp_author_slug: '' }
      }));
      
      toast.success(`Auteur "${values.name}" (ID: ${authorId}) configuré`);
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error('Erreur sauvegarde:', e);
      toast.error('Erreur lors de la sauvegarde');
    }
    setSaving(prev => ({ ...prev, [siteId]: false }));
  };
  
  // Vérifier si les valeurs ont changé
  const hasChanges = (siteId) => {
    const mapping = mappings[siteId];
    const edit = editValues[siteId];
    if (!edit) return false;
    if (!mapping) return edit.id || edit.name;
    return String(mapping.wp_author_id) !== edit.id || mapping.wp_author_name !== edit.name;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 text-sky-400 animate-spin" />
        <span className="ml-2 text-slate-400">Chargement...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="author-settings">
      <div className="flex items-center gap-2 mb-4">
        <User className="w-5 h-5 text-sky-400" />
        <h3 className="text-lg font-semibold text-white">
          {isAdmin ? "Configuration des auteurs" : "Mes noms d'auteur par site"}
        </h3>
      </div>
      
      <p className="text-sm text-slate-400 mb-4">
        Renseignez l'ID WordPress et le nom de l'auteur pour chaque site. 
        L'ID se trouve dans l'URL d'édition de l'utilisateur WordPress (ex: user-edit.php?user_id=<strong>123</strong>).
      </p>
      
      {sites.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-8 h-8 text-slate-500 mx-auto mb-2" />
            <p className="text-slate-400">Aucun site WordPress configuré</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sites.map(site => {
            const mapping = mappings[site.id];
            const editValue = editValues[site.id] || { id: '', name: '' };
            const isSaving = saving[site.id];
            const changed = hasChanges(site.id);
            
            return (
              <Card key={site.id} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex flex-col gap-4">
                    {/* En-tête site */}
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium truncate">{site.name}</p>
                        <p className="text-xs text-slate-500 truncate">{site.url}</p>
                      </div>
                      {mapping && !changed && (
                        <div className="flex items-center gap-1 text-emerald-400">
                          <Check className="w-4 h-4" />
                          <span className="text-xs">Configuré</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Champs de saisie */}
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex-1 min-w-[100px] max-w-[120px]">
                        <label className="block text-xs text-slate-400 mb-1">
                          ID WordPress
                        </label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="123"
                          value={editValue.id}
                          onChange={(e) => handleInputChange(site.id, 'id', e.target.value)}
                          className="bg-slate-900/50 border-slate-700 text-slate-100 h-9"
                          data-testid={`author-id-${site.id}`}
                        />
                      </div>
                      
                      <div className="flex-[2] min-w-[150px]">
                        <label className="block text-xs text-slate-400 mb-1">
                          Nom de l'auteur
                        </label>
                        <Input
                          type="text"
                          placeholder="Jean Dupont"
                          value={editValue.name}
                          onChange={(e) => handleInputChange(site.id, 'name', e.target.value)}
                          className="bg-slate-900/50 border-slate-700 text-slate-100 h-9"
                          data-testid={`author-name-${site.id}`}
                        />
                      </div>
                      
                      <Button
                        onClick={() => handleSaveMapping(site.id)}
                        disabled={isSaving || !editValue.id || !editValue.name}
                        className={`h-9 ${changed ? 'bg-sky-600 hover:bg-sky-500' : 'bg-slate-700 hover:bg-slate-600'}`}
                        data-testid={`save-author-${site.id}`}
                      >
                        {isSaving ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        <span className="ml-2 hidden sm:inline">
                          {isSaving ? 'Sauvegarde...' : 'Enregistrer'}
                        </span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AuthorSettings;
